import { t } from "@lingui/macro";
import { Button, Form, FormInstance, InputNumber, Space } from "antd";
import { FC, MouseEventHandler, useCallback, useEffect, useState } from "react";
import { SubscriptionPackage, SubscriptionPrice } from "../type";
import { SwapOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { translate } from "utils";

interface PriceItemProps {
	data: SubscriptionPrice;
	index: number;
	onchange?: (index: number, data: SubscriptionPrice) => void;
	onRemove?: (index: number) => void;
	form: FormInstance<SubscriptionPackage>;
}
export const DEFAULT_VALUE: SubscriptionPrice = {
	quotation: 1,
	price: 1,
	discount: 0,
};
const PriceItem: FC<PriceItemProps> = ({
	data,
	index,
	onchange,
	onRemove,
	form,
}) => {
	const [quotation, setQuotation] = useState<number>(data.quotation || 0);
	const [basePrice, setBasePrice] = useState(
		(data.price || 0) + (data.discount || 0),
	);
	const [discount, setDiscount] = useState(
		Math.round(((data.discount || 0) / basePrice) * 100),
	);
	const [discountUSD, setDiscountUSD] = useState(data.discount || 0);
	const finalPrice = basePrice - discountUSD;

	useEffect(() => {
		const price = basePrice - discountUSD;
		onchange?.(index, {
			quotation,
			discount: discountUSD,
			price,
		});
	}, [quotation, discountUSD, basePrice]);

	useEffect(() => {
		!data.price && handelBasePriceChange(data.price);
		!data.quotation && setQuotation(data.quotation || 0);
	}, [data]);

	const handelBasePriceChange = (value: number | null) => {
		const val = value || 0;
		setBasePrice(val);
		setDiscount(0);
		setDiscountUSD(0);
	};

	const handelDiscountChange = (value: number | null) => {
		const val = value || 0;
		setDiscount(val);
		setDiscountUSD((basePrice * val) / 100);
	};
	const handelDiscountUSDChange = (value: number | null) => {
		const val = value || 0;
		if (val >= basePrice) {
			setDiscount(0);
			setDiscountUSD(0);
			return;
		}
		const discountVal = Math.round((val / basePrice) * 100);
		setDiscountUSD(val);
		setDiscount(discountVal);
	};
	const handelQuotationChanged = (value: number | null) => {
		form.setFieldValue(["prices", index, "quotation"], value);
		setQuotation(value || 0);
	};
	const handleClickRemove: MouseEventHandler = () => {
		onRemove?.(index);
	};

	return (
		<Space className="w-full flex items-center flex-wrap">
			{t`For`}
			<Form.Item
				label={t`Quotation ${index + 1}`}
				name={["prices", index, "quotation"]}
			>
				<InputNumber
					addonAfter={t`Month`}
					className="w-32"
					min={1}
					onChange={handelQuotationChanged}
				/>
			</Form.Item>

			{t`will charge`}

			<InputNumber
				addonAfter="USD"
				className="w-40"
				value={basePrice}
				onChange={handelBasePriceChange}
				min={1}
			/>
			{t`or have discount`}
			<Form.Item
				label={t`Discount ${index + 1}`}
				name={["prices", index, "discount"]}
			>
				<InputNumber
					addonAfter="USD"
					className="w-32"
					value={discountUSD}
					onChange={handelDiscountUSDChange}
					min={0}
				/>
			</Form.Item>
			<InputNumber
				addonAfter="%"
				className="w-32"
				value={discount}
				onChange={handelDiscountChange}
				min={0}
				max={99}
			/>
			<SwapOutlined />
			<Form.Item
				label={t`Price ${index + 1}`}
				name={["prices", index, "price"]}
			>
				<InputNumber
					addonAfter="USD"
					className="w-40"
					disabled
					value={finalPrice}
				/>
			</Form.Item>
			{index ? (
				<DeleteOutlined
					className="text-[#828282] ml-2 text-lg cursor-pointer"
					onClick={handleClickRemove}
				/>
			) : null}
		</Space>
	);
};

interface PackagePricesProps {
	form: FormInstance<SubscriptionPackage>;
}
const PackagePrices: FC<PackagePricesProps> = ({ form }) => {
	const [prices, setPrices] = useState<SubscriptionPrice[]>([]);
	const data = form.getFieldValue("prices");
	useEffect(() => {
		!prices?.length && setPrices(data);
	}, [data?.length]);

	const handlePriceChanged = (index: number, data: SubscriptionPrice) => {
		setPrices(prev => {
			const newPrices = [...prev];
			newPrices[index] = data;
			form.setFieldValue("prices", newPrices);
			return newPrices;
		});
	};

	const handlePriceRemoved = (index: number) => {
		setPrices(prev => {
			const newPrices = [...prev];
			newPrices.splice(index, 1);
			form.setFieldValue("prices", newPrices);
			return newPrices;
		});
	};

	const handelAddPrice = () => {
		setPrices(prev => {
			const newPrices = [...prev];
			newPrices.push(DEFAULT_VALUE);
			form.setFieldValue("prices", newPrices);
			return newPrices;
		});
	};

	const isFreePackage = form.getFieldValue("id") && prices?.[0]?.price === 0;
	if (isFreePackage) {
		return null;
	}

	return (
		<div className="shadow-lg px-5 pt-5 pb-10 bg-white p-5 w-full">
			<div className="mt-3 flex justify-between  mb-4">
				<label className="font-semibold text-lg">
					{t`Price`}{" "}
					<span className="text-gray-400 font-normal">
						- {t`For 1 package can not over 5000$`}
					</span>
				</label>
			</div>
			<div className="w-full space-y-5">
				{prices?.map((el, index) => (
					<PriceItem
						data={el}
						index={index}
						key={index}
						onchange={handlePriceChanged}
						onRemove={handlePriceRemoved}
						form={form}
					/>
				))}
				<Button
					icon={<PlusOutlined />}
					type="primary"
					className="flex items-center ml-2 !text-gray-500 !border-gray-300 hover:bg-white focus:bg-white"
					htmlType="button"
					onClick={handelAddPrice}
				>
					{translate("Add")}
				</Button>
			</div>
		</div>
	);
};

export default PackagePrices;
